import { createElement } from '@syncfusion/ej2-base';
import { RIBBON_ID } from '../ribbon-base/ribbon-constants';
import { RibbonItemSize } from '@syncfusion/ej2-ribbon';
import { BulletListHelper } from '../../helper/bullet-list-helper';
// Numbering list constants
export const NUMBER_LIST_ID = '_number_list';
/**
 * NumberingGroup class for handling numbering list operations in Document Editor ribbon
 * @private
 */
export class NumberingGroup {
    /**
     * Constructor for NumberingGroup
     * @param {DocumentEditorContainer} container - DocumentEditorContainer instance
     */
    constructor(container) {
        // Track the last applied numbering style
        this.appliedNumberingStyle = 'arabic';
        // Event handlers for numbering options
        this.numberedAndBulletNoneClickHandler = this.bulletNoneClick.bind(this);
        this.numberedNumberDotClickHandler = this.numberedNumberDotClick.bind(this);
        this.numberedLowLetterClickHandler = this.numberedLowLetterClick.bind(this);
        this.numberedUpLetterClickHandler = this.numberedUpLetterClick.bind(this);
        this.numberedLowRomanClickHandler = this.numberedLowRomanClick.bind(this);
        this.numberedUpRomanClickHandler = this.numberedUpRomanClick.bind(this);
        this.container = container;
        this.ribbonId = this.container.element.id + RIBBON_ID;
        this.localObj = this.container.localObj;
    }
    get documentEditor() {
        return this.container.documentEditor;
    }
    /**
     * Get the numbering list split button item configuration
     * @returns {RibbonItemModel} - Numbering list split button item configuration
     * @private
     */
    getNumberingListItem() {
        const id = this.ribbonId;
        return {
            type: 'SplitButton',
            keyTip: 'N',
            allowedSizes: RibbonItemSize.Small,
            splitButtonSettings: this.createNumberingSplitButton(),
            id: id + NUMBER_LIST_ID,
            ribbonTooltipSettings: { content: this.localObj.getConstant('Numbering') }
        };
    }
    createNumberingSplitButton() {
        let numberIconCss = 'e-de-ctnr-numbering e-icons';
        if (this.container.enableRtl) {
            numberIconCss += ' e-de-flip';
        }
        // Create the HTML template for numbering dropdown
        const numberListDropDiv = createElement('div', {
            id: this.ribbonId + '_number_list_div',
            styles: 'width: 211px;height: auto;visibility: hidden'
        });
        const numberListDropUlTag = createElement('ul', {
            styles: 'visibility: visible; outline: 0px;',
            id: this.ribbonId + '_numberListMenu',
            className: 'e-de-floating-menu e-de-bullets-menu e-de-list-container e-de-list-thumbnail'
        });
        numberListDropDiv.appendChild(numberListDropUlTag);
        // Create numbering list options using BulletListHelper
        this.noneNumberTag = BulletListHelper.createNumberNoneListTag(numberListDropUlTag, this.localObj);
        this.noneNumberTag.addEventListener('click', this.numberedAndBulletNoneClickHandler);
        this.numberList = BulletListHelper.createNumberListTag(numberListDropUlTag, '1.', '2.', '3.');
        this.numberList.addEventListener('click', this.numberedNumberDotClickHandler);
        this.lowLetter = BulletListHelper.createNumberListTag(numberListDropUlTag, 'a.', 'b.', 'c.');
        this.lowLetter.addEventListener('click', this.numberedLowLetterClickHandler);
        this.upLetter = BulletListHelper.createNumberListTag(numberListDropUlTag, 'A.', 'B.', 'C.');
        this.upLetter.addEventListener('click', this.numberedUpLetterClickHandler);
        this.lowRoman = BulletListHelper.createNumberListTag(numberListDropUlTag, 'i.', 'ii.', 'iii.');
        this.lowRoman.addEventListener('click', this.numberedLowRomanClickHandler);
        this.upRoman = BulletListHelper.createNumberListTag(numberListDropUlTag, 'I.', 'II.', 'III.');
        this.upRoman.addEventListener('click', this.numberedUpRomanClickHandler);
        // Initialize element map for easier management
        this.numberElements = {
            'none': this.noneNumberTag,
            'number': this.numberList,
            'lowletter': this.lowLetter,
            'upletter': this.upLetter,
            'lowroman': this.lowRoman,
            'uproman': this.upRoman
        };
        return {
            target: numberListDropDiv,
            iconCss: numberIconCss,
            items: this.getNumberingItems(),
            content: this.localObj.getConstant('Numbering'),
            select: this.handleNumberingSelection.bind(this),
            beforeOpen: () => {
                numberListDropDiv.style.visibility = 'visible';
                const levelPattern = BulletListHelper.getCurrentListPattern(this.documentEditor);
                this.updateSelectedNumberedListType(levelPattern);
            },
            beforeClose: () => {
                numberListDropDiv.style.visibility = 'hidden';
                this.removeSelectedList();
            },
            click: () => {
                this.applyLastAppliedNumbering();
            },
            close: () => {
                this.refreshHomeSelection();
            }
        };
    }
    getNumberingItems() {
        return [
            { id: this.ribbonId + '_number-none', text: this.localObj.getConstant('None') },
            { id: this.ribbonId + '_number-arabic', text: this.localObj.getConstant('Arabic') },
            { id: this.ribbonId + '_number-lowletter', text: this.localObj.getConstant('Lower Letter') },
            { id: this.ribbonId + '_number-upletter', text: this.localObj.getConstant('Upper Letter') },
            { id: this.ribbonId + '_number-lowroman', text: this.localObj.getConstant('Lower Roman') },
            { id: this.ribbonId + '_number-uproman', text: this.localObj.getConstant('Upper Roman') }
        ];
    }
    removeSelectedList() {
        BulletListHelper.removeSelectedList(this.numberElements);
    }
    updateSelectedNumberedListType(listPattern) {
        BulletListHelper.updateSelectedNumberedListType(listPattern, this.numberElements);
    }
    /**
     * Apply the last used numbering style
     * @returns {void}
     * @private
     */
    applyLastAppliedNumbering() {
        switch (this.appliedNumberingStyle) {
            case 'arabic':
                this.numberedNumberDotClick();
                break;
            case this.ribbonId + '_lowroman':
                this.numberedLowRomanClick();
                break;
            case this.ribbonId + '_uproman':
                this.numberedUpRomanClick();
                break;
            case this.ribbonId + '_lowletter':
                this.numberedLowLetterClick();
                break;
            case this.ribbonId + '_upletter':
                this.numberedUpLetterClick();
                break;
            default:
                this.numberedNumberDotClick();
                break;
        }
    }
    handleNumberingSelection(args) {
        switch (args.item.id) {
            case this.ribbonId + '_number-none':
                this.bulletNoneClick();
                break;
            case this.ribbonId + '_number-arabic':
                this.numberedNumberDotClick();
                break;
            case this.ribbonId + '_number-lowroman':
                this.numberedLowRomanClick();
                break;
            case this.ribbonId + '_number-uproman':
                this.numberedUpRomanClick();
                break;
            case this.ribbonId + '_number-lowletter':
                this.numberedLowLetterClick();
                break;
            case this.ribbonId + '_number-upletter':
                this.numberedUpLetterClick();
                break;
        }
    }
    bulletNoneClick() {
        BulletListHelper.clearList(this.documentEditor);
        this.refreshHomeSelection();
    }
    numberedNumberDotClick() {
        this.applyNumbering('Arabic', 'arabic');
    }
    numberedLowLetterClick() {
        this.applyNumbering('LowLetter', 'lowletter');
    }
    numberedUpLetterClick() {
        this.applyNumbering('UpLetter', 'upletter');
    }
    numberedLowRomanClick() {
        this.applyNumbering('LowRoman', 'lowroman');
    }
    numberedUpRomanClick() {
        this.applyNumbering('UpRoman', 'uproman');
    }
    applyNumbering(pattern, style) {
        if (!this.documentEditor.isReadOnly && this.documentEditor.editorModule) {
            this.appliedNumberingStyle = style;
            const format = BulletListHelper.getLevelFormatNumber(this.documentEditor);
            this.documentEditor.editorModule.applyNumbering(format, pattern);
            setTimeout(() => {
                this.documentEditor.focusIn();
                this.refreshHomeSelection();
            }, 30);
        }
    }
    refreshHomeSelection() {
        const ribbonModule = this.container.ribbonModule;
        if (ribbonModule && ribbonModule.tabManager && ribbonModule.tabManager.homeTab) {
            ribbonModule.tabManager.homeTab.updateSelection();
        }
    }
    destroy() {
        // Remove event listeners for numbering tags
        if (this.noneNumberTag) {
            this.noneNumberTag.removeEventListener('click', this.numberedAndBulletNoneClickHandler);
        }
        if (this.numberList) {
            this.numberList.removeEventListener('click', this.numberedNumberDotClickHandler);
        }
        if (this.lowLetter) {
            this.lowLetter.removeEventListener('click', this.numberedLowLetterClickHandler);
        }
        if (this.upLetter) {
            this.upLetter.removeEventListener('click', this.numberedUpLetterClickHandler);
        }
        if (this.lowRoman) {
            this.lowRoman.removeEventListener('click', this.numberedLowRomanClickHandler);
        }
        if (this.upRoman) {
            this.upRoman.removeEventListener('click', this.numberedUpRomanClickHandler);
        }
        // Clear references
        this.noneNumberTag = null;
        this.numberList = null;
        this.lowLetter = null;
        this.upLetter = null;
        this.lowRoman = null;
        this.upRoman = null;
    }
}
